import { useEffect, useRef } from "react";

export const useRingback = (shouldRing) => {
    const ringbackRef = useRef(null);
    const audioCtxRef = useRef(null);

    const startRingback = () => {
        if (ringbackRef.current) return;

        const playTone = () => {
            try {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (!AudioContext) return;

                // Reuse or create context
                let ctx = audioCtxRef.current;
                if (!ctx || ctx.state === 'closed') {
                    ctx = new AudioContext();
                    audioCtxRef.current = ctx;
                }

                if (ctx.state === 'suspended') {
                    ctx.resume();
                }

                const t = ctx.currentTime;

                // US Standard Ringback: 440Hz + 480Hz
                const osc1 = ctx.createOscillator();
                const osc2 = ctx.createOscillator();
                const gain = ctx.createGain();

                osc1.type = "sine";
                osc1.frequency.setValueAtTime(440, t);

                osc2.type = "sine";
                osc2.frequency.setValueAtTime(480, t);

                // Mix them
                osc1.connect(gain);
                osc2.connect(gain);
                gain.connect(ctx.destination);

                // Volume
                gain.gain.setValueAtTime(0.15, t);

                // Timing: 2s ON, 4s OFF
                osc1.start(t);
                osc2.start(t);

                osc1.stop(t + 2);
                osc2.stop(t + 2);

                // We don't close context here to allow looping cleanly, but we must manage it.
                // Actually, for a simple loop, we can just schedule the next one or use setInterval.
            } catch (err) {
                console.warn("Ringback playback failed", err);
            }
        };

        playTone(); // Immediate
        const interval = setInterval(playTone, 6000); // 2s on + 4s off = 6s cycle
        ringbackRef.current = interval;
    };

    const stopRingback = () => {
        if (ringbackRef.current) {
            clearInterval(ringbackRef.current);
            ringbackRef.current = null;
        }
        if (audioCtxRef.current) {
            audioCtxRef.current.close().catch(() => { });
            audioCtxRef.current = null;
        }
    };

    useEffect(() => {
        if (shouldRing) {
            startRingback();
        } else {
            stopRingback();
        }
        return () => stopRingback();
    }, [shouldRing]);
};
